import React from 'react';
import { InfoTip } from './InfoTip';
import styles from './ToggleField.module.css';

export interface ToggleFieldProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
  /** Longer explanation shown in an info tooltip next to the label. */
  info?: string;
  disabled?: boolean;
  /** Toggle rows read best on their own line; set false to keep one inside a single grid column. */
  fullWidth?: boolean;
}

/**
 * A switch with its label on one row. The whole row toggles on click, but the label is linked with
 * `htmlFor` rather than wrapping the switch, so the optional info tooltip is not read as part of the
 * checkbox's accessible name.
 */
export const ToggleField: React.FC<ToggleFieldProps> = ({
  label,
  checked,
  onChange,
  hint,
  info,
  disabled = false,
  fullWidth = true,
}) => {
  const inputId = React.useId();
  const hintId = React.useId();

  const className = [
    styles.row,
    fullWidth ? styles.fullWidth : '',
    disabled ? styles.disabled : '',
  ].filter(Boolean).join(' ');

  const handleRowClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    // Clicks on the label or the input already toggle natively; clicks on the info icon must not toggle at all.
    const target = event.target as HTMLElement;
    if (target.closest('label, input, [data-info-tip]')) {
      return;
    }

    onChange(!checked);
  };

  return (
    <div className={className} onClick={handleRowClick}>
      <span className={styles.text}>
        <span className={styles.labelLine}>
          <label htmlFor={inputId} className={styles.label}>{label}</label>
          {info ? <InfoTip text={info} /> : null}
        </span>
        {hint ? <span id={hintId} className={styles.hint}>{hint}</span> : null}
      </span>
      <span className={styles.switch}>
        <input
          id={inputId}
          type="checkbox"
          className={styles.input}
          checked={checked}
          disabled={disabled}
          aria-describedby={hint ? hintId : undefined}
          onChange={event => onChange(event.target.checked)}
        />
        <span className={styles.slider} aria-hidden="true" />
      </span>
    </div>
  );
};
